/**
 * The transaction landing screen's data — list tabs' limits and operators, the
 * list / editor layout ids and labels, where the layout switch may sit, and the
 * screen preferences' limits.
 * ONE source, `listScreenConfig.json`, also read by the Python engine
 * (`list_screen_config.py`) and mirrored by the J2EE engine
 * (`ListScreenConfig.java`). Never write these values as literals.
 */
import LIST_SCREEN_CONFIG from './listScreenConfig.json'

export interface LayoutOption { id: string; label: string; hint?: string }

const CFG = LIST_SCREEN_CONFIG as {
  tabs: {
    maxTabs: number; maxConditionsPerTab: number; maxIdLength: number
    maxLabelLength: number; allId: string; allLabel: string; ops: string[]
  }
  listLayouts: LayoutOption[]
  editorLayouts: LayoutOption[]
  defaultEditorLayout: string
  legacyEditorViews: Record<string, string>
  layoutSwitchPlaces: LayoutOption[]
  defaultLayoutSwitchPlace: string
  screenPrefs: { maxScreens: number; maxTabIdLength: number; maxObjNameLength: number }
}

export const TAB_LIMITS = CFG.tabs
export const ALL_TAB_ID = CFG.tabs.allId
export const ALL_TAB_LABEL = CFG.tabs.allLabel
export const TAB_OPS: ReadonlySet<string> = new Set(CFG.tabs.ops)
export const LIST_LAYOUTS: readonly LayoutOption[] = CFG.listLayouts
export const EDITOR_LAYOUTS: readonly LayoutOption[] = CFG.editorLayouts
export const SCREEN_PREF_LIMITS = CFG.screenPrefs
export const LEGACY_EDITOR_VIEWS: Readonly<Record<string, string>> = CFG.legacyEditorViews

/** Where a person keeps the list layout switch: on the screen (`bar`, beside
 *  the tabs) or in the screen's ⋮ menu (`menu`). */
export type LayoutSwitchPlace = 'bar' | 'menu'
export const LAYOUT_SWITCH_PLACES: readonly LayoutOption[] = CFG.layoutSwitchPlaces
export const DEFAULT_LAYOUT_SWITCH_PLACE = CFG.defaultLayoutSwitchPlace as LayoutSwitchPlace

export function isLayoutSwitchPlace(v: unknown): v is LayoutSwitchPlace {
  return typeof v === 'string' && LAYOUT_SWITCH_PLACES.some((l) => l.id === v)
}

/** The record editor's layouts. */
export type EditorLayout = 'standard' | 'simple' | 'document' | 'compact'

export const DEFAULT_EDITOR_LAYOUT = CFG.defaultEditorLayout as EditorLayout

export function isEditorLayout(v: unknown): v is EditorLayout {
  return typeof v === 'string' && EDITOR_LAYOUTS.some((l) => l.id === v)
}

export function isListLayout(v: unknown): boolean {
  return typeof v === 'string' && LIST_LAYOUTS.some((l) => l.id === v)
}

export function listLayoutLabel(id: string): string {
  return LIST_LAYOUTS.find((l) => l.id === id)?.label || id
}

export function editorLayoutLabel(id: string): string {
  return EDITOR_LAYOUTS.find((l) => l.id === id)?.label || id
}
